import { ReactNode } from "react";

import Alert from "components/ui/alert/Alert";
import { UserSearchResult, useUsersGraphql } from "lib/hooks/api/users/useUsersGraphql";

// "Ada Lovelace (ada@acme.com)", with the whole thing a mailto link.
function AdminContact({ admin }: { admin: UserSearchResult }): JSX.Element {
  const name = `${admin.firstName} ${admin.lastName}`.trim();
  return <a href={`mailto:${admin.email}`}>{name ? `${name} (${admin.email})` : admin.email}</a>;
}

type Props = {
  /** What the reader can't do here, e.g. "Only company admins can manage SSO providers." */
  children: ReactNode;
};

/**
 * Tells a non-admin that a settings page is company-admin only, and which colleague to ask.
 *
 * Stotles staff are excluded from the list: they show as admins of whichever company they sit in,
 * and are not who a customer should be contacting. The lookup lives here rather than in the page so
 * it only runs when this actually renders — an admin never pays for it.
 */
export function CompanyAdminOnlyAlert({ children }: Props): JSX.Element {
  const { data: companyAdmins } = useUsersGraphql({
    isCompanyAdmin: true,
    isAdmin: false,
    active: true,
  });

  return (
    <Alert type="warning">
      {children}{" "}
      {!companyAdmins || companyAdmins.length < 1 ? (
        "Contact your CSM for assistance."
      ) : (
        <>
          Contact{" "}
          {companyAdmins.map((admin, index) => (
            <span key={admin.guid}>
              {index > 0 && (index === companyAdmins.length - 1 ? " or " : ", ")}
              <AdminContact admin={admin} />
            </span>
          ))}{" "}
          for assistance.
        </>
      )}
    </Alert>
  );
}
